import { BigCalendar } from 'lib/ui';
import { NavigateAction, View } from 'react-big-calendar';
import { customizeRange, getNavigationDate } from 'utils/builders/calendar';
import {
  STAFF_CALENDAR_MAX_HEIGHT,
  calendarIntervals
} from 'utils/constants/calendar';
import { localizer } from 'utils/builders/calendar';
import React, { useCallback, useMemo, useState } from 'react';
import { CalendarWrapper } from '../style';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import {
  createProviderSubBlocks,
  handleDroppedEvent,
  handleLowerTouchingEvents,
  handleUpperTouchingEvents,
  removeAllOverlappingEventsWithEditedOneAndAddNewEvent,
  removeOverlappingEvents,
  checkIfLessThanFiveMinsAndUpdate
} from './utils';
import { PlannerView, views } from 'utils/cache/calendar';
import { calendarDayMoment, calendarWeekMoment } from 'utils/format';
import { Event } from 'react-big-calendar';
import ModalSemantic from 'components/modalSemantic';
import EditForm from 'components/AvailabilityTemplateModalForm/EditForm';
import { ContentWrapper } from 'views/components/ui/card/card.style';
import { Appointment_Types } from 'components/AvailabilityTemplateModalForm/EditForm/dropdownOptions';
import { isTelehealthOnlySlotType } from 'utils/constants/telehealthSlotTypes';
import MainCalendarToolbar from 'components/calendar/calendarToolbar';
import { mapArrayOfIdsToObject } from 'utils/mappers/request/Provider';
import { IClinic } from 'model/v2/clinic';
import { getLocationColor, getSlotsStyleColor } from '../utils';
import { ILocation } from 'model/v2/location';
import { CalenderEvent } from 'model/v2/availabilityTemplate';
import ClinicBarCalendar from './ClinicBarCalendar';
import { getClinicsAbbreviation } from 'utils/mappers/availabilityTemplate';
import { SCROLL_TIME_CALENDAR } from 'utils/constants/calendar';
import SetShiftForm from 'components/AvailabilityTemplateModalForm/SetShiftForm';
import { setShiftData } from 'model/v2/setShiftBlock';
import { Alert } from '@material-ui/lab';
import { Typography } from '@material-ui/core';
import { Tooltip } from 'antd';
import { ClockCircleOutlined } from '@ant-design/icons';
import IsBookedBlockAuditModal from 'components/calendar/planner/PlannerModals/IsBookedBlockAuditModal';

interface IProps {
  events: CalenderEvent[];
  updateEvents: (
    value: CalenderEvent[] | ((prevVar: CalenderEvent[]) => CalenderEvent[])
  ) => void;
  bannerText?: string;
  department?: string;
  providerId?: number;
  alertText?: string;
  clinics?: IClinic[];
  providerDefaultClinicsIds?: number[];
  locations: ILocation[];
  selectedDate: Date;
  handleNavigation: (date: Date) => void;
  handleViewChange: () => void;
  disableNavigation?: boolean;
  handleAddAlternateModal: () => void;
  children?: React.ReactNode;
  isProviderAvailabilityMode?: boolean;
  editModeState: boolean;
  handleEditClick: () => void;
  providerSetShift: setShiftData[];
}

declare module 'react-big-calendar' {
  // This augments the Event interface everywhere in your project
  interface Event {
    schedulingShiftId?: number;
    type?: string;
    // Add any other custom fields you use
  }
}

const TemplateCalender: React.FC<IProps> = ({
  events,
  bannerText,
  updateEvents,
  providerId,
  alertText,
  clinics,
  providerDefaultClinicsIds,
  locations,
  selectedDate,
  handleNavigation,
  handleViewChange,
  disableNavigation = false,
  department,
  children,
  isProviderAvailabilityMode,
  editModeState,
  handleEditClick,
  providerSetShift
}: IProps) => {
  const view = PlannerView.plannerWeek;
  const DragAndDropCalendar = withDragAndDrop(BigCalendar);
  const [editModal, setEditModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<Event>();
  const [addedEvent, setAddedEvent] = useState<CalenderEvent | undefined>(
    undefined
  );
  const [showAlert, setShowAlert] = useState(true);
  const [auditAvailabilityId, setAuditAvailabilityId] = useState<number | null>(
    null
  );

  // A block-availability slot (the orange "BLOCK AVAILABILITY" cells). Only
  // these expose the isBookedBlock audit history.
  const isBlockAvailabilityEvent = (event: any) =>
    event?.type === 'Block Availability' ||
    event?.title === 'Block Availability' ||
    event?.type === 'Additional Availability' ||
    event?.title === 'Additional Availability' ||
    (event?.type === 'working' &&
      event?.schedulingShiftId !== null &&
      event?.schedulingShiftId !== undefined &&
      event?.schedulingShiftId > 0);

  // Custom react-big-calendar event renderer: keeps the default title and, for
  // block-availability slots with a persisted id, adds a history icon that opens
  // the isBookedBlock audit modal for that availability.
  const EventRenderer = useCallback(({ event, title }: any) => {
    const availabilityId = Number(event?.id);
    const showAuditIcon =
      isBlockAvailabilityEvent(event) && Number.isFinite(availabilityId);
    return (
      <div className="rbc-event-content-inner">
        <span className="rbc-event-title-text">{title}</span>
        {showAuditIcon && (
          <Tooltip title="View isBookedBlock audit">
            <button
              type="button"
              className="audit-history-btn"
              onMouseDown={e => {
                e.stopPropagation();
                e.nativeEvent.stopImmediatePropagation();
              }}
              onMouseUp={e => {
                e.stopPropagation();
                e.nativeEvent.stopImmediatePropagation();
              }}
              onClick={e => {
                e.stopPropagation();
                e.nativeEvent.stopImmediatePropagation();
                setAuditAvailabilityId(availabilityId);
              }}
            >
              <ClockCircleOutlined className="audit-history-icon" />
            </button>
          </Tooltip>
        )}
      </div>
    );
  }, []);

  const hasSetShift = events.some(
    event => event.title === 'Block Availability' && !event.isAdditionalShift
  );

  const formats = {
    dayFormat: 'ddd'
  };
  const [selectedTab, setSelectedTab] = useState<'availability' | 'block'>(
    'availability'
  );

  const clinicsAbbreviations = useMemo(() => {
    return getClinicsAbbreviation(events, clinics);
  }, [clinics, events]);

  // Ensure events are always fresh for the modal
  // const currentEvents = useMemo(() => {
  //   return events;
  // }, [events]);

  const customEventPropGetter = useCallback(
    (event: any) => {
      console.log('customEventPropGetter Vedavyas event', event);
      const appt_Type = Appointment_Types.find((appt_type: any) => {
        if (
          event.type === 'working' &&
          event.schedulingShiftId !== null &&
          event.schedulingShiftId !== undefined &&
          event.schedulingShiftId > 0
        ) {
          return appt_type.value === 'Block Availability';
        } else return appt_type.value === event.type;
      });
      if (appt_Type && appt_Type.colorRank <= 1) {
        return { className: `${event.type?.toLowerCase()}` };
      } else if (appt_Type && appt_Type.colorRank === 2) {
        const style = getSlotsStyleColor(appt_Type);
        return { style };
      } else {
        const style = getLocationColor(locations, event.location);

        if (event.type === 'evalslot' && event.location === locations[0]?.id) {
          return { className: 'evalslot' };
        } else {
          return { style };
        }
      }
    },
    [locations]
  );

  const titleAccessor = (event: Event) => {
    if (
      event.type === 'working' &&
      event.schedulingShiftId !== null &&
      event.schedulingShiftId !== undefined &&
      event.schedulingShiftId > 0
    ) {
      const eventTitle = 'Block Availability';
      return eventTitle.toUpperCase();
    } else return event.title !== undefined ? event.title.toUpperCase() : '';
  };

  const moveEvent = useCallback(
    ({ event, start, end }: any) => {
      // Don't allow moving block events
      if (
        event.type === 'Block Availability' ||
        event.type === 'Additional Availability'
      ) {
        return;
      }

      const idx = events.indexOf(event);
      if (end.getHours() < start.getHours()) {
        return;
      }
      if (event.start.getDay() === start.getDay()) {
        const updatedEvent: CalenderEvent = {
          ...event,
          start,
          end
        };
        events.splice(idx, 1, updatedEvent);
        removeOverlappingEvents(updatedEvent, events);
        handleDroppedEvent(updatedEvent, events);
        handleUpperTouchingEvents(updatedEvent, events);
        handleLowerTouchingEvents(updatedEvent, events);
        updateEvents([...events]);
      }
    },
    [events, updateEvents]
  );

  const resizeEvent = useCallback(
    ({ event, start, end }: any) => {
      // Don't allow resizing block events
      if (
        event.type === 'Block Availability' ||
        event.type === 'Additional Availability'
      ) {
        return;
      }

      const indexOfNewEvent = events.indexOf(event);
      const updatedEvent = {
        ...event,
        start,
        end
      };

      checkIfLessThanFiveMinsAndUpdate(updatedEvent);
      events.splice(indexOfNewEvent, 1, updatedEvent);
      removeOverlappingEvents(updatedEvent, events);
      handleUpperTouchingEvents(updatedEvent, events);
      handleLowerTouchingEvents(updatedEvent, events);
      updateEvents([...events]);
    },
    [updateEvents, events]
  );

  const onEventClick = useCallback((event: Event) => {
    setSelectedEvent(event);
    // Set default tab based on event type
    const calenderEvent = event as any;
    if (
      calenderEvent.type === 'Block Availability' ||
      calenderEvent.type === 'Additional Availability' ||
      (calenderEvent.type === 'working' &&
        calenderEvent.schedulingShiftId !== null &&
        calenderEvent.schedulingShiftId !== undefined &&
        calenderEvent.schedulingShiftId > 0)
    ) {
      setSelectedTab('block');
    } else {
      setSelectedTab('availability');
    }
    setEditModal(true);
  }, []);

  const onEditModalClose = useCallback(() => {
    if (addedEvent) {
      updateEvents((prev: CalenderEvent[]) => {
        let newEvents = prev;
        const idx = newEvents.indexOf(addedEvent);
        newEvents.splice(idx, 1);
        setAddedEvent(undefined);
        return newEvents;
      });
    }
    setEditModal(false);
  }, [addedEvent, updateEvents]);

  const onEditModalSubmit = useCallback(
    (data: any, submittedEvent: CalenderEvent) => {
      if (addedEvent) setAddedEvent(undefined);
      console.log(submittedEvent);
      const idx = events.indexOf(submittedEvent);
      const returnedClinics = mapArrayOfIdsToObject(data.clinics);
      const selectedOption = Appointment_Types.find(
        type => type.value === data.appt_type
      );
      const label = selectedOption?.label ?? data.appt_type;
      const submittedType = selectedOption?.value ?? data.appt_type;
      let newEvents: CalenderEvent[];
      if (idx > -1) {
        const updatedEvent: CalenderEvent = {
          id: submittedEvent?.id,
          title: label,
          type: submittedType,
          location: data.location,
          isAdmin: data.appt_type !== 'working',
          start: data.start,
          end: data.end,
          clinics: returnedClinics?.length === 0 ? undefined : returnedClinics,
          schedulingShiftId: data.schedulingShiftId,
          isAdditionalShift: data.isAdditionalShift
        };
        events.splice(idx, 1, updatedEvent);
      } else {
        submittedEvent.title = label;
        events.push(submittedEvent);
      }
      newEvents = [...events];

      const hasSublocks = data.sub_block_length > 0;
      if (hasSublocks) {
        data.recurringDays.forEach((day: number) => {
          let tempEvent = removeAllOverlappingEventsWithEditedOneAndAddNewEvent(
            newEvents,
            data,
            day,
            selectedDate,
            label,
            submittedType
          );

          newEvents = createProviderSubBlocks(
            newEvents,
            tempEvent,
            data,
            locations[0]
          );
        });

        updateEvents([...newEvents]);
        setEditModal(false);
      } else {
        data.recurringDays.forEach((day: number) => {
          removeAllOverlappingEventsWithEditedOneAndAddNewEvent(
            events,
            data,
            day,
            selectedDate,
            label,
            submittedType
          );
        });

        updateEvents([...events]);
        setEditModal(false);
      }
    },
    [addedEvent, events, updateEvents, locations]
  );

  const onSetShiftSubmit = useCallback(
    (newEventsArray: CalenderEvent[], originalEvent: CalenderEvent) => {
      if (addedEvent) setAddedEvent(undefined);
      onEditModalClose();

      let newEvents = [...events];

      if (
        originalEvent &&
        (originalEvent.type === 'Block Availability' ||
          originalEvent.type === 'Additional Availability' ||
          originalEvent.type === 'working')
      ) {
        newEvents = newEvents.filter(event => {
          const isBlockBeingEdited =
            event.type === originalEvent.type &&
            ((originalEvent.id && event.id === originalEvent.id) ||
              (originalEvent.schedulingShiftId &&
                event.schedulingShiftId === originalEvent.schedulingShiftId) ||
              (!originalEvent.id &&
                !originalEvent.schedulingShiftId &&
                event === originalEvent));

          return !isBlockBeingEdited;
        });
      }

      newEventsArray.forEach((item: any) => {
        removeAllOverlappingEventsWithEditedOneAndAddNewEvent(
          newEvents,
          item,
          item.start.getDay(),
          selectedDate,
          item.isAdditionalShift
            ? 'Additional Availability'
            : 'Block Availability',
          item.isAdditionalShift ? 'Additional Availability' : 'working'
        );
      });
      updateEvents([...newEvents]);
      setEditModal(false);
    },
    [addedEvent, events, updateEvents, locations, selectedDate] // Add selectedDate to dependencies
  );

  const onDelete = useCallback(
    (selectedEvent: CalenderEvent) => {
      if (
        selectedEvent.type === 'Block Availability' ||
        selectedEvent.type === 'Additional Availability' ||
        (selectedEvent.type === 'working' &&
          selectedEvent.schedulingShiftId !== null &&
          selectedEvent.schedulingShiftId !== undefined &&
          selectedEvent.schedulingShiftId > 0)
      ) {
        updateEvents((prev: CalenderEvent[]) => {
          let newEvents = prev;
          if (selectedEvent.schedulingShiftId) {
            newEvents = newEvents.filter(
              event =>
                !(
                  event.type === selectedEvent.type &&
                  event.schedulingShiftId === selectedEvent.schedulingShiftId
                )
            );
          } else {
            const idx = newEvents.indexOf(selectedEvent);
            if (idx > -1) {
              newEvents.splice(idx, 1);
            }
          }
          return [...newEvents];
        });
      } else {
        const idx = events.indexOf(selectedEvent);
        updateEvents((prev: CalenderEvent[]) => {
          let newEvents = prev;
          if (idx > -1) {
            newEvents.splice(idx, 1);
          }
          return [...newEvents];
        });
      }
      setEditModal(false);
    },
    [updateEvents, events]
  );

  const createNewEvent = useCallback(
    (start: Date, end: Date) => {
      const defaultApptType = Appointment_Types[0].value;

      let initialLocation = locations[0].id!;
      if (isTelehealthOnlySlotType(defaultApptType)) {
        initialLocation = 1;
      }

      const event: CalenderEvent = {
        end: end,
        start: start,
        title: defaultApptType,
        type: defaultApptType,
        location: initialLocation,
        isAdmin: false
      };
      onEventClick(event);
      updateEvents((prev: CalenderEvent[]) => {
        let newEvents = prev;
        setAddedEvent(event);
        newEvents.push(event);
        return newEvents;
      });
    },
    [locations, onEventClick, updateEvents]
  );

  const onCalenderClick = useCallback(
    ({ start, end }) => {
      if (selectedTab === 'block') {
        const event = {
          end,
          start,
          title: 'BLOCK',
          type: 'block',
          location: locations[0].id!,
          isAdmin: false
        };
        setSelectedEvent(event);
        setEditModal(true);
      } else {
        setSelectedTab('availability');
        createNewEvent(start, end);
      }
      setSelectedTab('availability');
    },
    [selectedTab, locations, onEventClick, updateEvents, createNewEvent]
  );

  const onNavigate = useCallback(
    (navigationAction: NavigateAction) => {
      const navigationDate = getNavigationDate(
        navigationAction,
        selectedDate,
        view
      );
      handleNavigation(navigationDate);
    },
    [handleNavigation, selectedDate, view]
  );

  const toolbarData = useMemo(() => {
    const data = {
      label:
        view === PlannerView.plannerWeek
          ? calendarWeekMoment(selectedDate)
          : calendarDayMoment(selectedDate),
      onNavigate: onNavigate,
      view: view,
      date: selectedDate,
      onView: handleViewChange
    };
    return data;
  }, [handleViewChange, onNavigate, selectedDate, view]);

  const customToolbar = useCallback(
    (): JSX.Element => (
      <>
        <MainCalendarToolbar
          bannerText={bannerText}
          customViewEnabled={true}
          data={toolbarData}
          range={customizeRange(
            selectedDate,
            view === views.PLANNER_WEEK
              ? (calendarIntervals.WEEK as View)
              : (calendarIntervals.DAY as View)
          )}
          handleNavigation={handleNavigation}
          disabled={disableNavigation}
          inEditMode={editModeState}
          handleEditClick={handleEditClick}
          alertText={alertText}
        />
        <ClinicBarCalendar clinics={clinicsAbbreviations} />
      </>
    ),

    [
      bannerText,
      toolbarData,
      selectedDate,
      view,
      handleNavigation,
      disableNavigation,
      clinicsAbbreviations,
      editModeState,
      handleEditClick
    ]
  );
  const calendarComponents = {
    toolbar: customToolbar,
    event: EventRenderer
  };

  const isAvailabilityEvent = (event: any) => {
    return (
      (event.type === 'Block Availability' ||
        event.type === 'Additional Availability' ||
        event.type === 'working' ||
        event.title === 'Block Availability' ||
        event.title === 'Additional Availability') &&
      event.schedulingShiftId !== null &&
      event.schedulingShiftId !== undefined &&
      event.schedulingShiftId > 0
    );
  };

  return (
    <>
      <CalendarWrapper>
        <DragAndDropCalendar
          selectable={editModeState ? true : false}
          draggableAccessor={(event: any) =>
            editModeState && !isAvailabilityEvent(event)
          }
          resizableAccessor={(event: any) =>
            editModeState && !isAvailabilityEvent(event)
          }
          views={[views.DAY, views.WEEK] as View[]}
          defaultView={'week'}
          defaultDate={selectedDate}
          toolbar={true}
          scrollToTime={SCROLL_TIME_CALENDAR}
          events={events}
          step={15}
          timeslots={4}
          localizer={localizer}
          formats={formats}
          style={{
            height: STAFF_CALENDAR_MAX_HEIGHT
          }}
          components={calendarComponents}
          titleAccessor={titleAccessor}
          onSelectEvent={
            editModeState
              ? (event: Event) => onEventClick(event as any)
              : () => null
          }
          onEventDrop={moveEvent}
          resizable
          onEventResize={resizeEvent}
          eventPropGetter={customEventPropGetter}
          onSelectSlot={editModeState ? onCalenderClick : () => null}
        />
        {children}
      </CalendarWrapper>

      {editModal && (
        <ContentWrapper>
          <ModalSemantic
            open={true}
            onClose={onEditModalClose}
            modalWidth={800}
            title={'Edit Availability Block'}
            trigger={<div></div>}
            content={
              <>
                {showAlert && (
                  <Alert
                    severity="info"
                    onClose={() => setShowAlert(false)}
                    style={{
                      background: '#E4D9FD',
                      color: '#000000',
                      marginBottom: '20px',
                      marginRight: '20px',
                      marginLeft: '20px'
                    }}
                  >
                    <Typography style={{ color: '#000000' }}>
                      If you switch between Availability and Set Shift
                      Availability without <br />
                      confirming, your current changes will not be saved.
                    </Typography>
                  </Alert>
                )}
                <div
                  style={{
                    display: 'flex',
                    gap: '20px',
                    borderBottom: '1px solid #ddd',
                    marginBottom: 16
                  }}
                >
                  <button
                    onClick={() => setSelectedTab('availability')}
                    style={{
                      border: 'none',
                      background: 'none',
                      padding: '10px 20px',
                      borderBottom:
                        selectedTab === 'availability'
                          ? '2px solid #7c3aed'
                          : 'none',
                      fontWeight:
                        selectedTab === 'availability' ? 'bold' : 'normal',
                      cursor: 'pointer'
                    }}
                  >
                    Availability
                  </button>
                  <button
                    onClick={() => setSelectedTab('block')}
                    style={{
                      border: 'none',
                      background: 'none',
                      padding: '10px 20px',
                      borderBottom:
                        selectedTab === 'block' ? '2px solid #7c3aed' : 'none',
                      fontWeight: selectedTab === 'block' ? 'bold' : 'normal',
                      cursor: 'pointer'
                    }}
                  >
                    Set Shift Availability
                  </button>
                </div>
                {/* Render Form Based on Tab */}
                {selectedTab === 'availability' ? (
                  <EditForm
                    onClose={onEditModalClose}
                    onSubmit={onEditModalSubmit}
                    onDelete={onDelete}
                    locations={locations}
                    data={selectedEvent}
                    providerId={providerId}
                    clinics={clinics}
                    providerDefaultClinicsIds={providerDefaultClinicsIds}
                    department={department}
                    isProviderAvailabilityMode={isProviderAvailabilityMode}
                  />
                ) : (
                  <SetShiftForm
                    key={`set-shift-${events.length}-${Date.now()}`}
                    onClose={onEditModalClose}
                    onSubmit={onSetShiftSubmit}
                    onDelete={onDelete}
                    data={selectedEvent}
                    clinics={clinics}
                    providerId={providerId}
                    hasSetShift={hasSetShift}
                    locations={locations}
                    providerDefaultClinicsIds={providerDefaultClinicsIds}
                    providerSetShift={providerSetShift}
                    existingEvents={events}
                    selectedDate={selectedDate}
                  />
                )}
              </>
            }
          />
        </ContentWrapper>
      )}

      {auditAvailabilityId != null && (
        <IsBookedBlockAuditModal
          isOpen={true}
          onClose={() => setAuditAvailabilityId(null)}
          availabilityId={auditAvailabilityId}
        />
      )}
    </>
  );
};

export default TemplateCalender;
